import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api.js';
import { useIsMobile } from '../lib/useIsMobile.js';
import Icon from './Icon.jsx';
import DashboardPicker from './DashboardPicker.jsx';
import ShareLinkEditor from './ShareLinkEditor.jsx';
import SharePreview from './SharePreview.jsx';
import ShareSendPanel from './ShareSendPanel.jsx';

// Shareable report links — the register + the create/edit sheet + who opened
// what. Dual-surface (scope: 'my' | 'admin-client'), one component: Howler runs it
// from Admin → the client → Share links, the client runs it from their own
// Settings. Backed by server/shareLinks.js.
//
// The link is LIVE: it re-runs the real queries on every open, so the register
// talks about "opens", never "sent a copy".

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' }) : '');
const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString('en-ZA', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');
const EXPIRY_CHOICES = [[7, '7 days'], [30, '30 days'], [90, '90 days'], [365, '1 year']];
const STATE_STYLE = {
  active: { bg: 'rgba(29,138,59,0.14)', fg: '#1d8a3b', label: 'Live' },
  expired: { bg: 'rgba(128,128,128,0.16)', fg: 'var(--muted)', label: 'Expired' },
  revoked: { bg: 'rgba(192,57,43,0.14)', fg: '#c0392b', label: 'Switched off' },
};

export default function ShareLinksManager({ entityId, scope = 'my' }) {
  const isMobile = useIsMobile();
  const [links, setLinks] = useState(null);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null); // {} for new, the link for edit
  const [statsFor, setStatsFor] = useState(null);
  const [previewFor, setPreviewFor] = useState(null);
  const [sendingId, setSendingId] = useState(''); // the row whose "Send by email" panel is open
  // The reports this client actually has — the server decides that, so the
  // dropdown and the create check can never disagree.
  const [reports, setReports] = useState([]);
  useEffect(() => {
    api.shareReportReports(scope === 'my' ? 'my' : 'admin-client', entityId)
      .then((r) => setReports(r.reports || []))
      .catch(() => setReports([]));
  }, [entityId, scope]);

  const load = useCallback(() => {
    api.shareReportList(scope === 'my' ? 'my' : 'admin-client', entityId)
      .then((r) => { setLinks(r.links || []); setError(''); })
      .catch((e) => { setLinks([]); setError(e.message); });
  }, [entityId, scope]);
  useEffect(() => { load(); }, [load]);

  if (previewFor) return <SharePreview scope={scope} link={previewFor} onClose={() => { setPreviewFor(null); load(); }} />;
  if (statsFor) return <ViewerStats scope={scope} link={statsFor} onClose={() => { setStatsFor(null); load(); }} />;
  if (editing) {
    return (
      <ShareLinkEditor
        scope={scope} entityId={entityId} link={editing.id ? editing : null}
        reports={reports}
        onClose={(changed) => { setEditing(null); if (changed) load(); }}
      />
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        <p style={{ fontSize: 13, color: 'var(--muted)', margin: 0, maxWidth: 560 }}>
          A live, read-only link to one report for someone with no Pulse login — a sponsor, venue or partner.
          Pick exactly which tiles they see, set an expiry, and see who opened it. The numbers refresh every time they load it.
        </p>
        <button style={primary} onClick={() => setEditing({})}>+ New link</button>
      </div>
      {error && <div style={{ fontSize: 13, color: '#c0392b', marginBottom: 10 }}>{error}</div>}
      {links === null ? null : links.length === 0 ? (
        <p style={{ color: 'var(--muted)', fontSize: 13, padding: '18px 0' }}>No shareable links yet.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {links.map((l) => (
            <LinkRow
              key={l.id} link={l} isMobile={isMobile}
              onEdit={() => setEditing(l)}
              onStats={() => setStatsFor(l)}
              onPreview={() => setPreviewFor(l)}
              scope={scope}
              sending={sendingId === l.id}
              onSend={() => setSendingId((cur) => (cur === l.id ? '' : l.id))}
              onToggle={async () => { await api.shareReportUpdate(scope === 'my' ? 'my' : 'admin-client', l.id, { revoked: l.state !== 'revoked' }); load(); }}
              onDelete={async () => {
                if (!window.confirm('Delete this link? Anyone holding it loses access immediately, and the record of who opened it goes too.')) return;
                await api.shareReportDelete(scope === 'my' ? 'my' : 'admin-client', l.id); load();
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function LinkRow({ link, isMobile, scope, onEdit, onStats, onPreview, onSend, sending, onToggle, onDelete }) {
  const [copied, setCopied] = useState(false);
  const st = STATE_STYLE[link.state] || STATE_STYLE.expired;
  const copy = async () => {
    try { await navigator.clipboard.writeText(link.url); } catch { window.prompt('Copy this link:', link.url); }
    setCopied(true); setTimeout(() => setCopied(false), 1600);
  };
  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 14.5, fontWeight: 700 }}>{link.title}</span>
            <span style={{ ...pill, background: st.bg, color: st.fg }}>{st.label}</span>
            <span style={{ ...pill, background: 'rgba(11,107,203,0.12)', color: '#0b6bcb' }}>
              <Icon name={link.mode === 'closed' ? 'lock' : 'email'} size={12} />
              {link.mode === 'closed' ? 'Pulse login' : 'Verified email'}
            </span>
            {(link.allowedEmails || []).length > 0 && (
              <span style={{ ...pill, background: 'rgba(109,40,217,0.12)', color: '#7c3aed' }}>
                <Icon name="lock" size={12} />
                Invite only · {link.allowedEmails.length}
              </span>
            )}
          </div>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
            {link.suiteName ? `${link.suiteName} · ` : ''}{link.dashboardTitle} · {link.visibleTiles.length} tile{link.visibleTiles.length === 1 ? '' : 's'} · expires {fmtDate(link.expiresAt)}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button style={mini} onClick={copy}>{copied ? '✓ Copied' : 'Copy link'}</button>
          <button style={mini} onClick={onPreview}>Preview &amp; arrange</button>
          {link.state === 'active' && (
            <button style={mini} onClick={onSend}>{sending ? 'Close' : 'Send by email'}</button>
          )}
          <button style={mini} onClick={onStats}>👁 {link.viewCount} open{link.viewCount === 1 ? '' : 's'}</button>
          <button style={mini} onClick={onEdit}>Edit</button>
          <button style={mini} onClick={onToggle}>{link.state === 'revoked' ? 'Switch on' : 'Switch off'}</button>
          <button style={{ ...mini, color: '#c0392b' }} onClick={onDelete}>Delete</button>
        </div>
      </div>
      {!isMobile && (
        <div style={{ marginTop: 8, fontSize: 11.5, color: 'var(--muted)', wordBreak: 'break-all' }}>
          {link.url} · created by {link.createdBy || 'Howler'} · last opened {link.lastViewedAt ? fmtWhen(link.lastViewedAt) : 'never'}
        </div>
      )}
      {sending && (
        <div style={{ marginTop: 10 }}>
          <ShareSendPanel scope={scope} link={link} />
        </div>
      )}
    </div>
  );
}

// ── Who opened it ─────────────────────────────────────────────────────────────
function ViewerStats({ scope, link, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    api.shareReportStats(scope === 'my' ? 'my' : 'admin-client', link.id).then(setData).catch((e) => setError(e.message));
  }, [scope, link.id]);

  return (
    <div>
      <button style={back} onClick={onClose}>← Back to links</button>
      <h3 style={{ fontSize: 16, fontWeight: 700, margin: '12px 0 2px' }}>{link.title}</h3>
      <p style={{ fontSize: 12.5, color: 'var(--muted)', marginBottom: 14, wordBreak: 'break-all' }}>{link.url}</p>
      {error && <div style={{ fontSize: 13, color: '#c0392b' }}>{error}</div>}
      {!data ? null : (
        <>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
            <Stat label="Opens" value={data.totals.views} />
            <Stat label="People" value={data.totals.viewers} />
            <Stat label="Verified" value={data.totals.verifiedViewers} />
          </div>
          {data.viewers.length === 0 ? (
            <p style={{ fontSize: 13, color: 'var(--muted)' }}>Nobody has opened this yet.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {data.viewers.map((v) => (
                <div key={v.id} style={card}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700 }}>
                        {v.name || v.email}
                        {!v.verified && <span style={{ ...pill, background: 'rgba(128,128,128,0.16)', color: 'var(--muted)', marginLeft: 8 }}>Never confirmed — saw no data</span>}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 3 }}>
                        {v.email}{v.phone ? ` · ${v.phone}` : ''}{v.company ? ` · ${v.company}` : ''}
                      </div>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--muted)', textAlign: 'right' }}>
                      <div>{v.viewCount} open{v.viewCount === 1 ? '' : 's'}</div>
                      <div>last {fmtWhen(v.lastSeenAt)}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div style={{ ...card, minWidth: 96, flex: '0 0 auto' }}>
      <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em' }}>{value}</div>
      <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>{label}</div>
    </div>
  );
}

const card = { background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: 13 };
const pill = { display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 980, whiteSpace: 'nowrap' };
const primary = { padding: '10px 16px', minHeight: 42, borderRadius: 10, border: 'none', background: 'var(--brand)', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' };
const mini = { padding: '7px 11px', minHeight: 36, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--card)', color: 'var(--text)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' };
const back = { background: 'none', border: 'none', color: 'var(--brand)', fontSize: 13, fontWeight: 700, cursor: 'pointer', padding: 0 };
